import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ToolBuilder } from '../../models/builders/tool.builder';
import { Tool } from '../../models/tool';

const toolBody = (tool: Tool) => ({
  hash: tool.hash,
  name: tool.name,
  erp: tool.erp,
  breakCheck: tool.breakCheck,
  refrigeration: tool.refrigeration,
  stockNumber: tool.stockNumber,
  toolCategory: tool.toolCategory,
  toolSettingModes: tool.toolSettingModes,
  toolSteps: tool.toolSteps,
  camTools: tool.camTools,
  toolRebuildOptions: tool.toolRebuildOptions,
  probeMovement: tool.probeMovement
});

@Injectable()
export class ToolApiService {

  private apiUrl: string;

  constructor(private httpClient: HttpClient) {
    this.apiUrl = `${environment.apiUrl}tools`;
  }

  getSettings(tools: string[]) {
    const params = new HttpParams().set('tools', tools.join(','));

    return this.httpClient.get(`${this.apiUrl}/settings`, { params }).pipe(map(ToolBuilder.buildSettingsFromList));
  }

  saveZollerTools(isToolFile: boolean, file: File) {
    const body = new FormData();
    body.append('toolFile', isToolFile.toString());
    body.append('file', file);

    return this.httpClient.post(`${this.apiUrl}/zoller`, body);
  }

  saveHaimerTools(isToolFile: boolean, file: File) {
    const body = new FormData();
    body.append('toolFile', isToolFile.toString());
    body.append('file', file);

    return this.httpClient.post(`${this.apiUrl}/haimer`, body);
  }

  validateZollerTools(isToolFile: boolean, file: File) {
    const body = new FormData();
    body.append('toolFile', isToolFile.toString());
    body.append('file', file);
    return this.httpClient.post(`${this.apiUrl}/zoller/validate`, body);
  }

  validateHaimerTools(isToolFile: boolean, file: File) {
    const body = new FormData();
    body.append('toolFile', isToolFile.toString());
    body.append('file', file);
    return this.httpClient.post(`${this.apiUrl}/haimer/validate`, body);
  }

  searchTools(search: string) {
    const params = new HttpParams().append('search', search);

    return this.httpClient.get(`${this.apiUrl}/search`, { params })
      .pipe(
        map(ToolBuilder.fromList)
      );
  }

  getSteps(tool: string) {
    const params = new HttpParams().append('hash', tool);
    return this.httpClient.get(`${this.apiUrl}/steps`, { params }).pipe(map(ToolBuilder.buildStepsFromList));
  }

  searchToolHolders(search: string) {
    const params = new HttpParams().append('search', search);

    return this.httpClient.get(`${this.apiUrl}/holder/search`, { params })
      .pipe(
        map(ToolBuilder.buildToolHolderFromList)
      );
  }

  createZollerTool(tool) {
    return this.httpClient.post(`${this.apiUrl}/zoller-tool`, toolBody(tool)).pipe(map(ToolBuilder.fromJson));
  }

  createHaimerTool(tool) {
    return this.httpClient.post(`${this.apiUrl}/haimer-tool`, toolBody(tool)).pipe(map(ToolBuilder.fromJson));
  }

  deleteTool(tool: Tool) {
    return this.httpClient.delete(`${this.apiUrl}/${tool.hash}`);
  }

  getTools() {
    return this.httpClient.get(`${this.apiUrl}/`).pipe(map(ToolBuilder.fromList));
  }

  getToolsByCategory(category: string) {
    const params = new HttpParams().append('category', category);
    return this.httpClient.get(`${this.apiUrl}/by-category`, { params }).pipe(map(ToolBuilder.fromList));
  }

  searchToolsByReference(search: string, notFind: string[]) {
    let params = new HttpParams().append('search', search);
    if (notFind) {
      params = params.set('notFind', notFind.toString());
    }
    return this.httpClient.get(`${this.apiUrl}/search-by-code`, { params }).pipe(map(ToolBuilder.fromList));
  }

  getTool(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/${hash}`).pipe(map(ToolBuilder.fromJson));
  }

  getToolSettingModesByTool(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/toolSettingModes/${hash}`).pipe(map(ToolBuilder.buildSettingModesFromList));
  }

  updateZollerTool(tool: Tool) {
    const body = toolBody(tool);
    return this.httpClient.put(`${this.apiUrl}/zoller-tool/${tool.hash}`, body);
  }

  updateHaimerTool(tool: Tool) {
    const body = toolBody(tool);
    return this.httpClient.put(`${this.apiUrl}/haimer-tool/${tool.hash}`, body);
  }

  getToolSteps(hash: string) {
    return this.httpClient.get(`${this.apiUrl}/steps/${hash}`).pipe(map(ToolBuilder.buildStepsFromList));
  }

}



// WEBPACK FOOTER //
// /var/www/borneo/prod/borneo_app/src/app/core/services/api/tool-api.service.ts